import { useEffect, useMemo } from 'react';
import { trackEvent } from '../services/analyticsService';
import { unplacedIngredients } from '../utils/resolveStepIngredients';
import type { RecipeIngredient, StepIngredientMap } from '../types/recipe';
import type { IngredientGroupInput } from '../utils/ingredientGrouping';

interface Args {
  recipeId?: string | number;
  /** The language the steps and ingredients are shown in, not the UI's. */
  language: string;
  stepTexts: string[];
  ingredients: RecipeIngredient[];
  stored?: StepIngredientMap[];
  ingredientGroups?: IngredientGroupInput[];
}

/** One report per recipe and language per session — a cook paging through steps is one look. */
const reported = new Set<string>();

/** Test seam. */
export const resetStepChipOrphanReports = () => reported.clear();

/**
 * The ingredients a recipe lists that no step's chips will ever show (#857).
 *
 * The chips are the only place a cook working step by step sees an
 * ingredient, and until #857 one that fell through every rule vanished with
 * nothing recorded: « ail, hachées » went missing from every French recipe
 * and was found by a shake report. This reports `step_chips_orphaned` with the
 * names, once per recipe and language, so the next blind spot shows up in
 * PostHog as a count by ingredient rather than as a cook's screenshot.
 */
export function useStepChipOrphans({
  recipeId,
  language,
  stepTexts,
  ingredients,
  stored,
  ingredientGroups,
}: Args): number[] {
  const orphans = useMemo(
    () => unplacedIngredients(stepTexts, ingredients, stored, ingredientGroups),
    [stepTexts, ingredients, stored, ingredientGroups],
  );

  useEffect(() => {
    if (!recipeId || !orphans.length) return;
    const key = `${recipeId}:${language}`;
    if (reported.has(key)) return;
    reported.add(key);
    trackEvent('step_chips_orphaned', {
      recipe_id: String(recipeId),
      language,
      orphan_count: orphans.length,
      ingredient_count: ingredients.length,
      step_count: stepTexts.length,
      has_stored_map: Boolean(stored?.length),
      orphans: orphans.slice(0, 10).map(i => ingredients[i]?.name ?? ''),
    });
  }, [recipeId, language, orphans, ingredients, stepTexts.length, stored]);

  return orphans;
}
